import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { message } from 'antd';
import { Layout, List, Prompt } from 'components/Page';
import {
  add, clear, dirty, init, list, load, remove, reset, update
} from 'actions';

/**
 * @description Map data and server flight
 * @param {state} Object
 * @param {owProps} Object
 */
const mapStateToProps = (state, ownProps) => {
  const extra = ownProps.reducer.extra ? {
    [ownProps.reducer.extra.key]: state.api[ownProps.reducer.extra.key].data.results || state.api[ownProps.reducer.extra.key].data
  } : {};

  return {
    item: ownProps.config.detail.format ? ownProps.config.detail.format({ ...state.api[ownProps.reducer.item.key].data, ...extra }) : { ...state.api[ownProps.reducer.item.key].data, ...extra },
    results: ownProps.config.list.format ? ownProps.config.list.format(state.api[ownProps.reducer.list.key].data.results) : state.api[ownProps.reducer.list.key].data.results,
    totalRecords: state.api[ownProps.reducer.list.key].data.count,
    inFlight: state.api[ownProps.reducer.list.key].inFlight,
    isDirty: state.api[ownProps.reducer.list.key].isDirty,
    formInFlight: state.api[ownProps.reducer.item.key].inFlight || (ownProps.reducer.extra ? state.api[ownProps.reducer.extra.key].inFlight : false),
    formError: state.api[ownProps.reducer.item.key].error
  };
};
/**
 * @description Dispatch create, loadList, load, deleteItem
 * @param {dispatch} Object
 */
const mapDispatchToProps = (dispatch, ownProps) => ({
  create() {
    dispatch(init(ownProps.reducer.item.key, {
      active: true,
      ...ownProps.config.init,
      v: Date.now()
    })); // attach timestamp for reset dataSource
    // if there is an extra data item clear it when initialising the main item
    if (ownProps.reducer.extra) {
      dispatch(reset(ownProps.reducer.extra.key));
    }
  },
  clearErrors() {
    dispatch(clear(ownProps.reducer.item.key));
  },
  resetItem() {
    dispatch(reset(ownProps.reducer.item.key));
    if (ownProps.reducer.extra) {
      dispatch(reset(ownProps.reducer.extra.key));
    }
  },
  loadList(params) {
    dispatch(list(
      ownProps.reducer.list.key, { ...params, ...ownProps.config.query }
    ))
      .then(() => (ownProps.onLoadList ? ownProps.onLoadList() : true));
  },
  loadItem(data) {
    const { id } = data;
    return dispatch(load(ownProps.reducer.item.key, { id, ...ownProps.config.query }))
      .then(() => (ownProps.onLoadItem ? ownProps.onLoadItem(id) : true));
  },
  reloadList() {
    dispatch(dirty(ownProps.reducer.list.key));
    if (ownProps.reducer.parent) {
      dispatch(dirty(ownProps.reducer.parent.key));
    }
  },
  saveItem(id, data) {
    return dispatch(id > 0 ? update(ownProps.reducer.item.key, { id, ...data }) : add(ownProps.reducer.item.key, { ...data, ...ownProps.config.detail.parent }));
  },
  deleteItem(id) {
    return dispatch(remove(ownProps.reducer.item.key, { id, ...ownProps.config.query }));
  }
});
/**
 * @description Launch the Prompt to Save or Discard changes
 * @param {Save} Function
 */
const promptSaveChanges = (save, cancel) => (
  Prompt({
    onOkClick: save,
    onCancelClick: cancel
  })
);
/**
 * LeaveTypes
 * @description Leave Type View
 * @version 1.0.0
 */
const Inner = ({
  canAdmin,
  classes,
  clearErrors,
  config,
  create,
  defaultSize,
  deleteItem,
  formError,
  formInFlight,
  isDirty,
  item,
  results,
  inFlight,
  loadItem,
  loadList,
  listOnTop,
  messaging,
  onPrepareData,
  onDelete,
  onDeleteCallback,
  onSuccessCallback,
  onFailureCallback,
  reloadList,
  saveItem,
  scroll,
  themeName,
  totalRecords
}) => {
  // Is the form in a state of edit or not
  const isEditMode = (item.id !== null && item.id > 0) || false;
  /**
   * @description Save the item and dislpay a relevant message
   * @param {id} Number
   * @param {data} Object
   * @param {prevData} Object
   * @triggers {reloadList}
   */
  const handleSave = (id, data, prevData = null) => {
    const preparedData = onPrepareData(data);
    const postSuccessCallback = () => {
      message.success(messaging.save.success(data));
      create(); // reset the loaded item
      reloadList(); // reload change list data
    };
    // null data aborts save, but still call callback
    if (preparedData === null) {
      return onSuccessCallback(null, data, item, postSuccessCallback);
    }
    return saveItem(id, preparedData)
      .then((response) => {
        onSuccessCallback(response, data, item, postSuccessCallback);
        return response;
      },
      (error) => {
        onFailureCallback(error, () => {
          message.error(messaging.save.failure(data));
          if (prevData) {
            clearErrors();
          }
        });
        return error;
      });
  };
  /**
   * @description Delete the item and dislpay a relevant message
   * @param {id} Number
   * @triggers {reloadList}
   */
  const handleDelete = id => deleteItem(id)
    .then((response) => {
      if (response.status === 204) {
        message.success(messaging.delete.success());
        create(); // remove item data from store
      }
      else {
        message.success(messaging.delete.disabled());
        // loadItem(response); // reload the current item to reflect the inactive field
      }
      // run the callback irrispective
      onDeleteCallback(response, () => {
        reloadList(); // reload the list of items to reflect the changes in related  data
        onDelete(response);
      });
    })
    .catch((error) => {
      message.error(messaging.delete.failure(error));
    });
  /**
   * @description Check if the form is dirty
   * @triggers {promptSaveChanges}
   */
  const handleFormClose = (currentItem, previousItem) => {
    // Prompt to save the changed form data
    promptSaveChanges(() => handleSave(previousItem.id, previousItem, true),
      () => {
      });
  };

  /**
   * @description Get List of list
   * @returns {List}
   */
  const getList = () => (
    <List
      canAdmin={canAdmin}
      className="bhp-container"
      dataSource={results}
      isDirty={isDirty}
      inFlight={inFlight}
      onLoadList={loadList}
      onAddItemClick={create}
      onLoad={create} // provide an initialised object
      onRowClick={loadItem}
      onDeleteItem={handleDelete}
      scroll={scroll}
      selectedItemId={item.id}
      showInlineEdit={canAdmin}
      showInlineDelete={canAdmin}
      totalRecords={totalRecords}
      {...config.list}
    />
  );
  /**
   * @description Get LeaveType  detail tabs
   * @returns {Detail}
   */
  const getDetail = () => (
    <div className="bhp-container">
      {
        config.detail.component({
          canAdmin,
          dataSource: item,
          formError,
          inFlight: formInFlight,
          isEditMode,
          parent: config.detail.parent,
          onSaveClick: handleSave,
          onCancelClick: create,
          onFormClose: handleFormClose,
          onPopupOk: clearErrors,
          popupError: true,
          themeName,
          title: config.detail.title
        })
      }
    </div>
  );
  return (
    // Layout Component to build out the Page
    <Layout
      classes={classes}
      component1={listOnTop ? getDetail() : getList()}
      component2={listOnTop ? getList() : getDetail()}
      split="horizontal"
      defaultSize={defaultSize}
    />
  );
};

Inner.propTypes = {
  canAdmin: PropTypes.bool,
  classes: PropTypes.string,
  clearErrors: PropTypes.func,
  config: PropTypes.object,
  create: PropTypes.func,
  deleteItem: PropTypes.func,
  formError: PropTypes.object,
  isDirty: PropTypes.bool,
  formInFlight: PropTypes.bool,
  item: PropTypes.object,
  results: PropTypes.array,
  inFlight: PropTypes.bool,
  loadItem: PropTypes.func,
  loadList: PropTypes.func,
  listOnTop: PropTypes.bool,
  onPrepareData: PropTypes.func,
  onDelete: PropTypes.func,
  onDeleteCallback: PropTypes.func,
  onSuccessCallback: PropTypes.func,
  onFailureCallback: PropTypes.func,
  messaging: PropTypes.object,
  saveItem: PropTypes.func,
  scroll: PropTypes.object,
  reloadList: PropTypes.func,
  themeName: PropTypes.string,
  totalRecords: PropTypes.number,
  defaultSize: PropTypes.string
};

Inner.defaultProps = {
  canAdmin: false,
  classes: '',
  config: {},
  clearErrors() {
  },
  create() {
  },
  defaultSize: '45%',
  deleteItem() {
  },
  formError: {},
  isDirty: false,
  formInFlight: false,
  item: {},
  results: [],
  inFlight: false,
  loadItem() {
  },
  loadList() {
  },
  listOnTop: true,
  messaging: {
    delete: {
      disabled: () => 'Item Disabled.',
      failure: error => `Delete Failed. ${error}`,
      success: () => 'Delete Successful.'
    },
    save: {
      failure: data => `Saving '${data.name}' failed`,
      success: data => `Saving '${data.name}' was successful`
    }
  },
  onPrepareData(data) {
    return data;
  },
  onDelete(response) {
    return response;
  },
  onSuccessCallback(response, data, original, callback) {
    callback();
  },
  onDeleteCallback(response, callback) {
    callback();
  },
  onFailureCallback(response, callback) {
    callback();
  },
  saveItem() {
  },
  scroll: {},
  reloadList() {
  },
  themeName: '',
  totalRecords: 0
};

export default connect(mapStateToProps, mapDispatchToProps)(Inner);
